/**
 * requireAuth — loader React Router qui garde les routes authentifiées.
 *
 * @remarks
 * Stratégie en 2 étapes :
 *
 * 1. Si `isAuthenticated` (token déjà en mémoire) → autorise (`null`).
 *
 * 2. Sinon, **tente un silent refresh** via le cookie HTTP-only
 *    `refresh_token`. Cas typique : reload de la page (l'access token vit en
 *    mémoire, donc disparait au reload, mais le cookie subsiste et l'API
 *    peut régénérer un access token frais sans demander mot de passe).
 *
 *    - Refresh OK → store réhydraté, autorise.
 *    - Refresh KO → session vraiment expirée, redirect `/login`.
 *
 * **Déduplication** : si plusieurs loaders demandent le refresh en parallèle
 * (rare mais possible), `attemptSilentRefresh()` fait UN seul appel partagé.
 *
 * La redirection garde la page demandée (`/login?redirect=…`) : après
 * connexion, on y revient au lieu d'atterrir sur `/tests` (SAAS-1266).
 */

import { redirect, type LoaderFunctionArgs } from 'react-router-dom';
import { useAuthStore } from '@/app/stores/use-auth-store';
import { loginPath } from '@/lib/auth/redirect';
import { attemptSilentRefresh } from '@/lib/auth/refresh';

/** `/login`, avec la page que le loader gardait. */
export function loginRedirect(args: LoaderFunctionArgs): Response {
  const url = new URL(args.request.url);
  return redirect(loginPath(`${url.pathname}${url.search}`));
}

export async function requireAuth(args: LoaderFunctionArgs): Promise<null | Response> {
  if (useAuthStore.getState().isAuthenticated) return null;

  const refreshed = await attemptSilentRefresh();
  if (refreshed) return null;

  return loginRedirect(args);
}
